import React from "react";
import { Zap, Phone, ArrowRight, ShieldCheck } from "lucide-react";
import { HERO } from "@/constants/testIds";
import { BUSINESS, LOGO_URL } from "@/data/siteData";

const scrollTo = (id) => (e) => {
  e.preventDefault();
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
};

export default function Hero() {
  return (
    <section
      id="top"
      data-testid={HERO.root}
      className="relative overflow-hidden pe-noise"
    >
      {/* Background grid + radial glow */}
      <div className="absolute inset-0 pe-grid-bg" />
      <div className="absolute inset-0 pe-radial-gold" />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[var(--pe-black)]" />

      <div className="container-pe relative z-10 pt-24 pb-32 md:pt-32 md:pb-44">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 pe-rise">
            <img
              src={LOGO_URL}
              alt="Perez Electric"
              className="h-32 sm:h-40 md:h-48 w-auto object-contain mb-6 -ml-3 sm:-ml-4 md:-ml-6 drop-shadow-[0_8px_24px_rgba(250,204,21,0.18)]"
            />
            <div className="pe-eyebrow mb-6">
              Licensed Electrical Contractors
            </div>

            <h1 className="font-display text-white text-5xl sm:text-6xl lg:text-7xl xl:text-8xl leading-[0.95]">
              POWERED BY
              <br />
              <span className="text-[var(--pe-gold)]">PRECISION.</span>
              <br />
              WIRED FOR
              <br />
              <span className="relative inline-block">
                <span className="text-[var(--pe-gold)]">EXCELLENCE.</span>
                <span
                  aria-hidden
                  className="absolute -right-6 -top-3 text-[var(--pe-blue)]"
                >
                  <Zap className="h-7 w-7" />
                </span>
              </span>
            </h1>

            <p className="mt-8 max-w-xl text-[var(--pe-muted)] text-base md:text-lg leading-relaxed">
              From EV chargers and panel upgrades to full residential
              re-wires and commercial fit-ups — Perez Electric handles every
              circuit with code-grade craftsmanship and zero shortcuts.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-3">
              <a
                href="#quote"
                onClick={scrollTo("quote")}
                data-testid={HERO.ctaQuote}
                className="pe-btn pe-btn-primary"
              >
                Request a Free Quote <ArrowRight className="h-4 w-4" />
              </a>
              <a
                href="#services"
                onClick={scrollTo("services")}
                data-testid={HERO.ctaServices}
                className="pe-btn pe-btn-outline"
              >
                Explore Services
              </a>
              <a
                href={BUSINESS.phoneHref}
                data-testid={HERO.ctaCall}
                className="pe-btn pe-btn-blue"
              >
                <Phone className="h-4 w-4" /> {BUSINESS.phone}
              </a>
            </div>

            <div className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-3 text-xs font-mono tracking-[0.22em] uppercase text-[var(--pe-muted)]">
              <span className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-[var(--pe-gold)]" /> Licensed
              </span>
              <span className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-[var(--pe-gold)]" /> Insured
              </span>
              <span className="flex items-center gap-2">
                <Zap className="h-4 w-4 text-[var(--pe-blue)]" /> EV Specialist
              </span>
              <span className="flex items-center gap-2">
                <Zap className="h-4 w-4 text-[var(--pe-blue)]" /> Code-Compliant Work
              </span>
            </div>
          </div>

          {/* Visual column */}
          <div className="lg:col-span-5 relative">
            <div className="relative mx-auto max-w-md">
              <div className="pe-image-plate aspect-[4/5]">
                <img
                  src="https://images.unsplash.com/photo-1593941707882-a5bba14938c7?auto=format&fit=crop&w=1200&q=80"
                  alt="EV charger plugged into vehicle"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[var(--pe-black)] via-transparent to-transparent" />
              </div>
              {/* Floating logo card */}
              <div className="absolute -bottom-8 -left-8 hidden md:block">
                <div className="pe-card !p-4 flex items-center gap-3 bg-[var(--pe-ink)]">
                  <img
                    src={LOGO_URL}
                    alt="Perez Electric"
                    className="h-14 w-14 object-contain rounded-sm bg-[var(--pe-black)] border border-[var(--pe-line)] p-0.5"
                  />
                  <div>
                    <div className="font-display tracking-[0.18em] text-white text-sm">PEREZ ELECTRIC</div>
                    <div className="font-mono text-[10px] tracking-[0.22em] text-[var(--pe-gold)] uppercase">Licensed · Insured</div>
                  </div>
                </div>
              </div>
              {/* Floating stat */}
              <div className="absolute -top-6 -right-6 hidden md:block">
                <div className="pe-card !p-4 text-right">
                  <div className="font-display text-3xl text-[var(--pe-gold)]">EV READY</div>
                  <div className="font-mono text-[10px] tracking-[0.22em] text-[var(--pe-muted)] uppercase">Charger Installs</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bolt line marquee */}
      <div className="relative border-y border-[var(--pe-line)] bg-[var(--pe-ink)] py-4 overflow-hidden">
        <div className="pe-marquee-track flex whitespace-nowrap gap-12 will-change-transform">
          {[...Array(2)].map((_, dup) => (
            <div key={dup} className="flex items-center gap-12 pr-12">
              {[
                "EV CHARGERS",
                "PANEL UPGRADES",
                "POT LIGHTS",
                "SMART HOMES",
                "STANDBY GENERATORS",
                "EMERGENCY RESPONSE",
                "COMMERCIAL FIT-UPS",
                "CODE INSPECTIONS",
                "HOT TUB WIRING",
              ].map((t, i) => (
                <span key={`${dup}-${i}`} className="flex items-center gap-4 text-[var(--pe-muted)] font-display tracking-[0.22em] text-sm">
                  <Zap className="h-4 w-4 text-[var(--pe-gold)]" /> {t}
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
